import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PRODUCTS, IMAGES } from '../mock';

const isWidebody = (p) => {
  const s = `${p.slug || ''} ${p.name || ''}`.toLowerCase();
  if (!s.includes('widebody') && !s.includes(' kit') && !s.includes('kit ')) return false;
  if (s.includes('wheel arches') || s.includes('arches')) return false;
  if (s.includes('velocity') || s.includes('forged')) return false;
  return true;
};

const BodyKits = () => {
  const kits = useMemo(() => PRODUCTS.filter(isWidebody).sort((a, b) => a.price - b.price), []);

  return (
    <div className="bg-white text-neutral-900 min-h-screen">
      {/* Hero */}
      <section
        className="relative pt-40 pb-16 text-white"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.88), rgba(0,0,0,0.7)), url(${IMAGES.workshop})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">
            FORTUNE BLACK LABEL
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight">FULL BODY KITS.</h1>
          <p className="text-neutral-300 mt-4 max-w-2xl">
            Complete bolt-on widebody conversions designed in-house and built to fit. Each kit ships
            with everything you need to transform the stance and silhouette of your build &mdash;
            front, rear, and fender flares as one cohesive package.
          </p>
          <div className="mt-6 text-[12px] text-neutral-400 font-display tracking-[0.22em]">
            {kits.length} KIT{kits.length !== 1 ? 'S' : ''} AVAILABLE
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <section className="border-b border-neutral-100 py-4">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-sm text-neutral-500">
          <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-neutral-900">Body Kits</span>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {kits.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.slug}`}
                data-testid={`bodykit-card-${p.slug}`}
                className="group block"
              >
                <div className="relative aspect-[4/3] bg-neutral-100 overflow-hidden">
                  {p.images && p.images[0] ? (
                    <img
                      src={p.images[0]}
                      alt={p.name}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-neutral-400 font-display tracking-[0.22em] text-xs">
                      NO IMAGE
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-black text-white text-[10px] font-display tracking-[0.22em] px-2 py-1">
                    {(p.brand || 'FORTUNE').toUpperCase()}
                  </span>
                </div>
                <div className="pt-4">
                  <h3 className="font-display text-lg tracking-tight group-hover:text-[#FF6B00] transition-colors">
                    {p.name}
                  </h3>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="font-display text-xl">${p.price.toLocaleString()}</span>
                    <span className="text-[11px] text-neutral-500">USD</span>
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 text-[12px] font-display tracking-[0.22em] text-neutral-800 group-hover:text-[#FF6B00] transition-colors">
                    VIEW DETAILS <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {kits.length === 0 && (
            <div className="text-center py-20 text-neutral-500">
              We&rsquo;re currently restocking body kits. Please check back soon or{' '}
              <Link to="/contact" className="text-[#FF6B00] underline">contact our team</Link>.
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-neutral-50 border-t border-neutral-100 py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-2">
              DON&rsquo;T SEE YOUR VEHICLE?
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight">
              CUSTOM WIDEBODY DEVELOPMENT.
            </h2>
            <p className="text-neutral-600 mt-2 max-w-md">
              We design and build bespoke widebody packages for any chassis. Talk to our team about
              your project.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="bodykit-contact-cta"
            className="inline-flex items-center gap-3 bg-black hover:bg-neutral-900 text-white px-7 py-4 font-display tracking-[0.22em] text-sm font-bold transition-colors"
          >
            START A BUILD <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BodyKits;
